/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Dropdown, Grid, Menu, Modal, Popconfirm, Row, Space, Table, Tooltip, Typography } from 'antd';
import { CopyOutlined, EllipsisOutlined, EyeOutlined, FolderViewOutlined, HighlightOutlined } from '@ant-design/icons';
import Permissioned from '../Permissioned';
import ActivityForm from '../Forms/ActivityForm';
import ActivityTypeTag from '../UI/ActivityTypeTag';
import ActivityCloneForm from '../Forms/ActivityCloneForm';
import BulkActionsCloneModal from '../Modals/BulkActionsCloneModal';
import MainActivityButton from '../UI/MainActivityButton';
import BulkActionsModal from '../../components/Modals/BulkActionsModal';
import DeleteWithReferencesModal from '../../components/Modals/DeleteWithReferencesModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import BotLanguageSelect from '../UI/BotLanguageSelect';

// Misc imports
import { useCreateActivitiesMutation, useCreateActivityMutation, useDeleteActivitiesMutation, useDeleteActivityMutation, useFetchActivitiesQuery, useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import { ACTIVITY_TYPES, PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE, SEARCH_ACTIVITY_TYPES } from '../../constants/main';
import { NODE_TYPES } from '../../constants/flowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { NAV_BOT } from '../../constants/navigation';
import { useReferenceChecker } from '../../utils/hooks/useReferenceChecker';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { cloneFlowToTargetLanguage } from '../../utils/transform/cloneFlowContentToTargetLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useLazyFetchStoryVariablesQuery } from '../../store/story/storyApi';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';
import ActivitiesSearchByInput from '../UI/ActivitiesSearchByInput';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text } = Typography;

const BULK_ACTIONS = {
  DELETE: 'delete',
  CLONE: 'clone'
};

const ActivitiesTable = () => {
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({});
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const isMobileSize = screens.xs;
  const { currentBotLanguage, primaryBotLanguage, isEditingPrimaryLanguage } = useBotLanguage();
  const { handleMouseDown, isDragging } = useDragDetection();
  const { data: bots } = getCurrentQueryState('fetchBots', { orgId });

  const { data: activities, isFetching: isFetchingActivities } = useFetchActivitiesQuery({ orgId, tenantId }, { skip: searchParams.get('activity') });
  const [{ isLoading: isUpdatingActivity }] = useUpdateActivityMutation({ fixedCacheKey: 'update-activity-main-button-shared' });
  const [deleteActivity, { isLoading: isDeletingActivity }] = useDeleteActivityMutation();
  const [deleteActivities, { isLoading: isDeletingActivities }] = useDeleteActivitiesMutation();
  const [, { isLoading: isCreatingActivity }] = useCreateActivityMutation({ fixedCacheKey: 'create-activity-shared' });
  const [createActivities, { isLoading: isCreatingActivities }] = useCreateActivitiesMutation({ fixedCacheKey: 'create-activities-shared' });
  const [fetchStoryVariables] = useLazyFetchStoryVariablesQuery();
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);

  const [tableDataSource, setTableDataSource] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({ search: '', type: '' });
  const [sortedInfo, setSortedInfo] = useState({ order: 'ascend', columnKey: 'name' });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [activityTypesInResults, setActivityTypesInResults] = useState(null);
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [activitiesToShow, setActivitiesToShow] = useState(0);
  const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [isCloneActivitiesModalOpen, setIsCloneActivitiesModalOpen] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [selectedActivityIdToDelete, setSelectedActivityIdToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const hasEditAccess = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);

  const {
    resetReferences,
    checkForActivityReferences,
    isLoading: isFindingReferences,
    relatedIds,
    deploymentsRelatedIds
  } = useReferenceChecker();

  const columns = [
    {
      title: t('Flow Name'),
      key: 'name',
      sorter: (a, b) => a.name[a.name[currentBotLanguage] ? currentBotLanguage : primaryBotLanguage]?.localeCompare(b.name[b.name[currentBotLanguage] ? currentBotLanguage : primaryBotLanguage]),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: record => {
        const nameToDisplay = record.name[currentBotLanguage] || record.name[primaryBotLanguage];
        return nameToDisplay ? (
          <Space>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={filteredInfo.type === 'name' ? [filteredInfo.search] : []}
              autoEscape
              textToHighlight={nameToDisplay}
            />

            {
              // If there are other search results within the nested values, show an indicator
              !nameToDisplay?.includes(filteredInfo.search) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        ) : <Text type="secondary">{t('(no name)')}</Text>;
      }
    },
    {
      title: t('Type'),
      key: 'type',
      width: 130,
      filters: activityTypesInResults?.map(status => ({
        text: status.label,
        value: status.value
      })),
      onFilter: (value, record) => record.type === value,
      filterIcon: useTableFilterIcon,
      render: record => (<ActivityTypeTag type={record.type} />)
    },
    {
      title: t('Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md'],
      width: 150,
      sorter: (a, b) => a.createdAt - b.createdAt,
      sortOrder: sortedInfo.columnKey === 'createdAt' && sortedInfo.order,
      render: createdAt => {
        if (createdAt) {
          return moment(createdAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Updated at'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['sm'],
      width: 150,
      sorter: (a, b) => a.updatedAt - b.updatedAt,
      sortOrder: sortedInfo.columnKey === 'updatedAt' && sortedInfo.order,
      render: updatedAt => {
        if (updatedAt) {
          return moment(updatedAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: isMobileSize ? 145 : 215,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        if (userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
          return (
            <Space onClick={(e) => e.stopPropagation()}>
              <Tooltip overlayClassName="pointer-events-none" title={t('Edit flow graph')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<EditIcon />}
                  loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                  onClick={() => onActivitySelect(record.id)}
                />
              </Tooltip>

              <Popconfirm
                title={t('Are you sure you want to delete this flow?')}
                okText={t('Yes')}
                cancelText={t('No')}
                placement="topRight"
                onConfirm={() => checkForReferencesAndDelete(record.id)}
              >
                <Tooltip overlayClassName="pointer-events-none" title={t('Delete flow')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<DeleteIcon />}
                    loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                    disabled={!isEditingPrimaryLanguage}
                  />
                </Tooltip>
              </Popconfirm>

              {
                !isMobileSize ? (
                  <Tooltip overlayClassName="pointer-events-none" title={t('Clone flow')}>
                    <Button
                      shape="circle"
                      type="text"
                      icon={<CopyOutlined />}
                      loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                      onClick={() => openCloneModal(record)}
                      disabled={!isEditingPrimaryLanguage}
                    />
                  </Tooltip>
                ) : null
              }

              {
                !isMobileSize ? (
                  <Tooltip overlayClassName="pointer-events-none" title={t('Preview flow')}>
                    <Button
                      shape="circle"
                      type="text"
                      icon={<EyeOutlined />}
                      loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                      onClick={() => previewActivity(record)}
                    />
                  </Tooltip>
                ) : null
              }

              <MainActivityButton
                buttonType="text"
                disabled={record.type === ACTIVITY_TYPES.USER_FEEDBACK || !isEditingPrimaryLanguage}
                chaptersource={record}
                botId={tenantId}
              />

              {
                isMobileSize ? (
                  <Dropdown
                    trigger="click"
                    overlay={
                      <Menu
                        onClick={(item) => {
                          switch (item.key) {
                            case 'clone':
                              openCloneModal(record);
                              break;
                            case 'preview':
                              previewActivity(record);
                              break;
                            default:
                              break;
                          }
                        }}
                        items={[
                          {
                            key: 'clone',
                            icon: <CopyOutlined />,
                            label: t('Clone')
                          },
                          {
                            key: 'preview',
                            icon: <EyeOutlined />,
                            label: t('Preview')
                          }
                        ]}
                      />
                    }
                  >
                    <Button
                      shape="circle"
                      type="text"
                      icon={<EllipsisOutlined />}
                      loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                    />
                  </Dropdown>
                ) : null
              }
            </Space>
          );
        } else if (userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission))) {
          return (
            <Space>
              <Tooltip overlayClassName="pointer-events-none" title={t('View flow graph')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<FolderViewOutlined/>}
                  loading={isFetchingActivities || isCreatingActivity || isUpdatingActivity || isDeletingActivity || isDeletingActivities}
                  onClick={() => onActivitySelect(record.id)}
                />
              </Tooltip>
            </Space>
          );
        }
      }
    }
  ].filter(column => !column.hidden);

  useEffect(() => {
    if (!activities?.items) {
      return;
    }

    const key = 'type';
    const uniqueActivityTypes = [...new Map(activities.items.map(item => [item[key], {
      label: t(item[key], { context: 'activity type' }),
      value: item[key]
    }])).values()];

    uniqueActivityTypes.sort((a, b) => a.value.localeCompare(b.value));

    setActivityTypesInResults(uniqueActivityTypes);
  }, [activities?.items]);

  const onDropdownSelect = (key) => {
    let newSelectedActivities = [];
    if (key === 'all') {
      newSelectedActivities = activities?.items?.map((item) => item.id);
    }
    setSelectedActivityIds(newSelectedActivities);
  };

  const onRowSelectChange = (newSelectedActivities) => {
    setSelectedActivityIds(newSelectedActivities);
  };

  const deleteBulkAction = async () => {
    const isBulkActionSuccessful = await deleteActivities({
      orgId,
      tenantId,
      data: selectedActivityIds
    }).unwrap();

    if (isBulkActionSuccessful) {
      // Refetch variables list in case the activity had node with variables that were deleted
      fetchStoryVariables({ orgId, tenantId });
      setSelectedActivityIds([]);
      setIsBulkActionsModalOpen(false);
      setShowReferenceModal(false);
    }
  };

  const handleBulkActionsMenuClick = (e) => {
    if (e.key === BULK_ACTIONS.CLONE) {
      openBulkActionsCloneActivitiesModal();
    } else if (e.key === BULK_ACTIONS.DELETE) {
      openBulkActionsModal();
    }
  };

  //Bulk Actions Modal Handlers
  const openBulkActionsCloneActivitiesModal = () => {
    setSelectedActivities(bulkActionsCloneActivitiesModalData());
    setActivitiesToShow(selectedActivityIds.length > 5 ? 5: selectedActivityIds.length);
    setIsCloneActivitiesModalOpen(true);
  };

  const closeBulkActionsCloneActivitiesModal = () => {
    setIsCloneActivitiesModalOpen(false);
  };

  const onBulkActionsCloneActivitiesModalShowMore = () => {
    setActivitiesToShow(activitiesToShow + 5);
  };

  const bulkActionsCloneActivitiesModalData = () => {
    const activitiesToClone = selectedActivityIds?.map((selectedActivity) => {
      return activities?.items?.find(item => item.id === selectedActivity);
    });
    return activitiesToClone;
  };

  const openBulkActionsModal = () => {
    setActivitiesToShow(selectedActivityIds.length > 5 ? 5 : selectedActivityIds.length);
    setIsBulkActionsModalOpen(true);
  };

  const closeBulkActionsModal = () => {
    setIsBulkActionsModalOpen(false);
  };

  const handleBulkActions = async () => {
    const hasReferencesRes = await checkForActivityReferences(selectedActivityIds);
    if(!hasReferencesRes) {
      deleteBulkAction();
    } else {
      setShowReferenceModal(true);
    }
  };

  const onBulkActionsModalShowMore = () => {
    setActivitiesToShow(activitiesToShow + 5);
  };

  const getBulkActionsModalData = () => {
    const activitiesToDelete = selectedActivityIds?.map((selectedActivityId) => {
      return activities?.items?.find(item => item.id === selectedActivityId);
    });
    return activitiesToDelete;
  };

  const handleCloneSubmission = async (values) => {
    const payload = selectedActivities.map(activityToClone => {
      return cloneFlowToTargetLanguage(bots, tenantId, values, activityToClone, selectedActivities.length === 1 );
    });

    const isSaveSuccessful = await createActivities({ orgId, tenantId: values.tenantId, data: payload }).unwrap();

    if (isSaveSuccessful) {
      openConsoleAlertMessage({
        type: 'success',
        message: selectedActivities.length > 1
          ? t('Flows cloned!')
          : t('Flow cloned!'),
        duration: 10,
        secondaryButton: {
          action: () => {
            navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: values.tenantId })}?view=behavior&tab=flows`);
          },
          title: t('View')
        }
      });

      // After create is done, fetch the latest data
      // dispatch(fetchActivities(orgId, tenantId));

      // Trigger callback to notify parent component of success
      closeBulkActionsCloneActivitiesModal();
    } else {
      openConsoleAlertMessage({ type: 'error', message: t('There was an error cloning the flow.') });
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedActivityIds,
    onChange: onRowSelectChange,
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => onDropdownSelect('all')
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => onDropdownSelect('none')
      }
    ]
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    setSortedInfo(sorter);
    setPageSize(pagination.pageSize);
  };

  const onActivityCreated = async (createdActivity) => {
    // TODO: Change page to last page
    closeCreateModal();
    // Navigate to the newly-created activity right-away
    onActivitySelect(createdActivity.id);
  };

  const onActivityCloned = () => {
    // TODO: Change page to last page
    closeCloneModal();
  };

  const onActivitySelect = (activityId) => {
    searchParams.set('activity', activityId);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const onActivityDelete = async (activityId) => {
    const isDeleted = await deleteActivity({ orgId, tenantId, activityId }).unwrap();

    // If delete was successful, refresh the list with the latest data
    if (isDeleted) {
      if (selectedActivityIds.includes(activityId)) {
        const newSelectedActivities = [...selectedActivityIds];
        const reqIndex = newSelectedActivities.indexOf(activityId);
        newSelectedActivities.splice(reqIndex, 1);
        setSelectedActivityIds(newSelectedActivities);
      }
      // Refetch variables list in case the activity had node with variables that were deleted
      fetchStoryVariables({ orgId, tenantId });
    }
    resetReferences();
    setSelectedActivityIdToDelete(null);
    setShowReferenceModal(false);
  };

  /**
   * Proceed to actual deletion of activities after checking for references in chaptersources
   * @param {String} activityIds the activities to be deleted
   */
  const checkForReferencesAndDelete = async (activityIds) => {
    setSelectedActivityIdToDelete(activityIds);
    const res = await checkForActivityReferences(activityIds);
    if(!res) {
      onActivityDelete(activityIds);
    } else {
      setShowReferenceModal(true);
    }
  };

  const previewActivity = (activityToPreview) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      previewEntryPoint: activityToPreview.content?.nodes?.find(node => node.type === NODE_TYPES.INTRO)?.id
    });
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openCloneModal = (record) => {
    setIsCloneModalOpen(true);

    if (record) {
      setSelectedActivity(record);
    }
  };
  const closeCloneModal = () => {
    setIsCloneModalOpen(false);
  };

  const onReferencesModalCancel = () => {
    setShowReferenceModal(false);
    resetReferences();
  };

  return (
    <Fragment>
      <Row gutter={24} className="mb-2">
        <Col flex="auto">

          <Space className="flex-wrap mb-2">
            <BotLanguageSelect
              size="large"
              selectStyle={{ width: 170 }}
            />
            <ActivitiesSearchByInput
              items = {activities?.items || []}
              onFilteredItemsReturn = {(filteredItems, searchValue, searchByType) => {
                setFilteredInfo({
                  type: searchByType,
                  search: searchValue
                });

                setTableDataSource(filteredItems);
              }}
              activityType = {SEARCH_ACTIVITY_TYPES.FLOWS}
            />
          </Space>
        </Col>

        <Col>
          <Space>
            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu
                    onClick={handleBulkActionsMenuClick}
                    items={[
                      {
                        label: t('Clone Selected'),
                        key: BULK_ACTIONS.CLONE
                      },
                      {
                        label: t('Delete Selected'),
                        key: BULK_ACTIONS.DELETE
                      }
                    ]}
                  />
                }
                disabled={!selectedActivityIds.length > 0}
              >
                <Button
                  block
                  size="large"
                >
                  {t('Bulk Actions')}
                  <ArrowDownIcon />
                </Button>
              </Dropdown>
            </Permissioned>

            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              <Button
                icon={<PlusIcon />}
                type="primary"
                className="float-right"
                size="large"
                onClick={openCreateModal}
                loading={isCreatingActivity}
              >
                {t('Add Flow')}
              </Button>
            </Permissioned>
          </Space>

        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            rowSelection={hasEditAccess ? rowSelection : null}
            onRow={(record) => ({
              onMouseDown: handleMouseDown,
              onClick: () => {
                if(!isDragging()) {
                  onActivitySelect(record.id);
                }
              }
            })}
            rowClassName="cursor-pointer"
            size="small"
            pagination={{
              size: 'default',
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Flows')}: ${total}`
            }}
            rowKey="id"
            columns={columns}
            dataSource={tableDataSource}
            loading={isFetchingActivities}
            onChange={handleTableChange}
          />
        </Col>
      </Row>

      <Modal
        title={t('Create New Flow')}
        open={isCreateModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
      >
        <ActivityForm onSuccess={onActivityCreated} />
      </Modal>

      <Modal
        title={t('Clone flow')}
        open={isCloneModalOpen}
        onCancel={closeCloneModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
      >
        <ActivityCloneForm activityToClone={selectedActivity} onSuccess={onActivityCloned} />
      </Modal>

      <BulkActionsModal
        title={`${selectedActivityIds.length === 1 ? 'flow' : 'flows'}`}
        action={t('Delete')}
        isModalOpen={isBulkActionsModalOpen}
        data={getBulkActionsModalData()}
        onModalClose={closeBulkActionsModal}
        onModalConfirm={handleBulkActions}
        loader={isFetchingActivities || isDeletingActivity || isDeletingActivities || isFindingReferences}
        onShowMore={onBulkActionsModalShowMore}
        entityCount={activitiesToShow}
        displayProperty={['name', primaryBotLanguage]}
      />

      <BulkActionsCloneModal
        isModalOpen={isCloneActivitiesModalOpen}
        dataToBeCloned={selectedActivities}
        onModalClose={closeBulkActionsCloneActivitiesModal}
        loader={isFetchingActivities || isCreatingActivity || isCreatingActivities}
        onShowMore={onBulkActionsCloneActivitiesModalShowMore}
        cloneEntityCount={activitiesToShow}
        onSubmit={handleCloneSubmission}
        configOverrides= {{
          modalTitle: 'Clone Flows',
          tenantIdDescription: 'Select the agent you want to add the cloned flows to.',
          newNameDescription: 'The cloned flow name',
          submitBtnTitle: 'Clone Flows'
        }}
      />

      {/* Modal to see the references of one or more activities to be deleted and proceed or not the the deletion */}
      <DeleteWithReferencesModal
        isModalOpen={showReferenceModal}
        title={
          selectedActivityIds.length > 1
            ? t('Delete Flows')
            : t('Delete Flow')
        }
        description={
          selectedActivityIds.length > 1
            ? t('The items you are trying to delete are referenced to the following Automated Answers/Flows/Deployments. Are you sure you want to delete it?')
            : t('The item you are trying to delete is referenced to the following Automated Answers/Flows/Deployments. Are you sure you want to delete it?')
        }
        onModalClose={onReferencesModalCancel}
        onModalConfirm={() => {
          if (selectedActivityIds.length) {
            deleteBulkAction();
          } else {
            onActivityDelete(selectedActivityIdToDelete);
          }
        }}
        loader={isDeletingActivity || isDeletingActivities}
        chapterSourcesData={relatedIds}
        deploymentsData={deploymentsRelatedIds}
      />
    </Fragment>
  );
};

export default ActivitiesTable;
